import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractLedger } from '@wsphere/warranties/domain';
import { firstValueFrom } from 'rxjs';

export interface Dimension {
  id: string;
  title: string;
  isScalar?: boolean;
  isPricing: boolean;
  isSystem: boolean;
}

export interface Element {
  id: string;
  title: string;
  dimensionId: string;
  parentId: string | null;
  policyId?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  parent?: Element | null;
  dimension?: Dimension;
  // Fee Type elements only — the default fee recipient (see ContractLedger.RecipientAssignment).
  recipientRole?: ContractLedger.PartyType | null;
  recipientPartyId?: string | null;
}

export interface CellElement {
  elementId: string;
  element?: Element;
}

export type CellValueType = 'FIXED' | 'FORMULA';

export interface CellFormula {
  percent: number;
  feeTypeElementIds: string[];
}

export interface Cell {
  id: string;
  planId: string;
  /** Always 0 for a FORMULA cell. */
  value: number;
  valueType?: CellValueType;
  formula?: CellFormula | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  recipientRole?: ContractLedger.PartyType | null;
  recipientPartyId?: string | null;
  elements: CellElement[];
}

export interface Paginated<T> {
  items: T[];
  meta?: { totalItems?: number };
}

export interface CoverageCalculation {
  coverageId: string;
  coverageTitle: string;
  price: number;
  cellId: string | null;
  cellValue: number | null;
  cellFound: boolean;
  intersections: Array<{
    dimensionId: string;
    dimensionTitle: string;
    elementId: string;
    elementTitle: string;
    isPricing: boolean;
    cellId?: string | null;
    cellValue?: number | null;
    elements?: Array<{
      dimensionId: string;
      dimensionTitle: string;
      elementId: string;
      elementTitle: string;
    }>;
    formula?: { percent: number; feeTypeTitles: string[]; baseAmount: number };
    /** The cell's Fee Type, null when it has none. */
    feeType?: { elementId: string; title: string } | null;
    /** The cell's effective recipient, null when none is assigned. */
    recipient?: {
      party: ContractLedger.Party;
      partyRecordId: string | null;
      source: ContractLedger.RecipientSource;
    } | null;
  }>;
  notes: string[] | null;
}

/** A fee-recipient party as listed for management (see ListPartiesQueryHandler). */
export type PartyListItem = ContractLedger.PartyRecord & {
  /** Each Fee Type default and cell override naming this party, one readable line each. */
  usage: string[];
};

/** A matrix cell whose amount would issue with no configured recipient. */
export interface RecipientReviewCell {
  cellId: string;
  feeType: { elementId: string; title: string } | null;
  reason: 'UNASSIGNED' | 'NO_FEE_TYPE' | 'UNRESOLVABLE';
}

export interface PricingPreview {
  planId: string;
  planTitle: string;
  termId: string;
  termTitle: string;
  termDuration: number;
  price: number;
  cellValue: number | null;
  cellFound: boolean;
  intersections: CoverageCalculation['intersections'];
  notes: string[] | null;
  dynamicRatingUsed: boolean;
  flooredToZero: boolean;
  flooredNotes: string[];
  /** One-time (initiation) fees, in cents — charged once, so never part of `price`. */
  oneTimePrice: number;
  oneTimeIntersections: CoverageCalculation['intersections'];
}

@Injectable()
export class RatingMatrixService {
  private dimensionsByTypeCache = new Map<string, Promise<Dimension[]>>();

  constructor(private http: HttpClient) {}

  async listDimensions(params?: { page?: number; limit?: number; policyId?: string; propertySetId?: string }) {
    return firstValueFrom(
      this.http.get<Paginated<Dimension>>('/v1/rating/dimensions', {
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 100,
          ...(params?.policyId && { policyId: params.policyId }),
          ...(params?.propertySetId && { propertySetId: params.propertySetId }),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      })
    );
  }

  // Dimensions for a set filtered by scalar/categorical, cached per (set, type).
  // `isScalar` only comes from this endpoint, not the property-set payload.
  private listDimensionsByType(propertySetId: string, scalar: boolean): Promise<Dimension[]> {
    const cacheKey = `${propertySetId}:${scalar ? 'scalar' : 'categorical'}`;
    const cached = this.dimensionsByTypeCache.get(cacheKey);
    if (cached) return cached;

    const promise = this.listDimensions({ propertySetId, limit: 100 })
      .then(res => (res.items ?? []).filter(dimension => !!dimension.isScalar === scalar))
      .catch(err => {
        // Don't cache failures — allow a retry on the next open.
        this.dimensionsByTypeCache.delete(cacheKey);
        throw err;
      });

    this.dimensionsByTypeCache.set(cacheKey, promise);
    return promise;
  }

  async listScalarDimensions(propertySetId: string): Promise<Dimension[]> {
    return this.listDimensionsByType(propertySetId, true);
  }

  async listCategoricalDimensions(propertySetId: string): Promise<Dimension[]> {
    return this.listDimensionsByType(propertySetId, false);
  }

  // Drop cached dimensions — both scalar and categorical — (one set, or all) so the
  // next fetch is fresh — used when the picker reopens after dimensions may have changed.
  invalidateDimensions(propertySetId?: string) {
    if (propertySetId) {
      this.dimensionsByTypeCache.delete(`${propertySetId}:scalar`);
      this.dimensionsByTypeCache.delete(`${propertySetId}:categorical`);
    } else {
      this.dimensionsByTypeCache.clear();
    }
  }

  async listElements(
    dimensionId: string,
    params?: { page?: number; limit?: number; policyId?: string; search?: string; parentId?: string | null }
  ) {
    return firstValueFrom(
      this.http.get<Paginated<Element>>('/v1/rating/elements', {
        params: {
          dimensionId,
          page: params?.page ?? 1,
          limit: params?.limit ?? 100,
          ...(params?.policyId && { policyId: params.policyId }),
          ...(params?.search && { search: params.search }),
          ...(params?.parentId !== undefined ? { parentId: params.parentId ?? 'null' } : {}),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      })
    );
  }

  async createElement(payload: {
    dimensionId: string;
    title: string;
    parentId?: string | null;
    description?: string | null;
  }) {
    return firstValueFrom(
      this.http.post<Element>('/v1/rating/elements', {
        dimensionId: payload.dimensionId,
        title: payload.title,
        parentId: payload.parentId ?? null,
        description: payload.description ?? null,
      })
    );
  }

  async syncVehicleMakeModelElements(assetInfo: Record<string, unknown> | null | undefined): Promise<void> {
    const propertyValues =
      assetInfo && typeof assetInfo['propertyValues'] === 'object'
        ? (assetInfo['propertyValues'] as Record<string, unknown>)
        : assetInfo;

    const vehicle = propertyValues?.['roadVehicle'];
    if (!vehicle || typeof vehicle !== 'object') return;

    const vehicleValues = vehicle as Record<string, unknown>;
    const makeValue = typeof vehicleValues['roadVehicleMake'] === 'string' ? vehicleValues['roadVehicleMake'] : null;
    const modelValue = typeof vehicleValues['roadVehicleModel'] === 'string' ? vehicleValues['roadVehicleModel'] : null;
    const trimValue = typeof vehicleValues['trim'] === 'string' ? vehicleValues['trim'] : null;

    const normalizeValue = (value: string) => value.trim().replace(/\s+/g, ' ');
    const make = makeValue ? normalizeValue(makeValue) : null;
    const model = modelValue ? normalizeValue(modelValue) : null;
    const trim = trimValue ? normalizeValue(trimValue) : null;

    if (!make || !model) return;

    try {
      const dimensions = await this.listDimensions({ limit: 100 });
      const normalizeDimensionTitle = (value: string) => value.toLowerCase().replace(/[\s\-_/]+/g, '');
      const makeModelDimension = dimensions.items.find(dimension => {
        const normalizedTitle = normalizeDimensionTitle(dimension.title);
        return (
          (normalizedTitle.includes('make') && normalizedTitle.includes('model')) ||
          normalizedTitle === 'makemodel' ||
          normalizedTitle.includes('ymm')
        );
      });

      if (!makeModelDimension) return;

      const makeCandidates = await this.listElements(makeModelDimension.id, {
        limit: 20,
        search: make,
        parentId: null,
      });

      let makeElement = makeCandidates.items.find(
        element => !element.parentId && element.title.toLowerCase().trim() === make.toLowerCase().trim()
      );
      if (!makeElement) {
        makeElement = await this.createElement({ dimensionId: makeModelDimension.id, title: make });
      }

      const makeElementId = makeElement?.id;
      if (!makeElementId) return;

      const modelCandidates = await this.listElements(makeModelDimension.id, {
        limit: 20,
        search: model,
        parentId: makeElementId,
      });
      let modelElement = modelCandidates.items.find(
        element => element.title.toLowerCase().trim() === model.toLowerCase().trim()
      );
      if (!modelElement) {
        modelElement = await this.createElement({
          dimensionId: makeModelDimension.id,
          title: model,
          parentId: makeElementId,
        });
      }

      if (!trim || !modelElement?.id) return;

      const trimCandidates = await this.listElements(makeModelDimension.id, {
        limit: 20,
        search: trim,
        parentId: modelElement.id,
      });

      const trimExists = trimCandidates.items.some(
        element => element.title.toLowerCase().trim() === trim.toLowerCase().trim()
      );

      if (!trimExists) {
        await this.createElement({
          dimensionId: makeModelDimension.id,
          title: trim,
          parentId: modelElement.id,
        });
      }
    } catch (err) {
      console.error('Failed to sync vehicle make/model/trim elements:', err);
    }
  }

  async listCells(params?: { planId?: string; page?: number; limit?: number }) {
    return firstValueFrom(
      this.http.get<Paginated<Cell>>('/v1/rating/cells', {
        params: {
          ...(params?.planId && { planId: params.planId }),
          page: params?.page ?? 1,
          limit: params?.limit ?? 100,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      })
    );
  }

  async createCell(payload: {
    planId: string;
    value: number;
    valueType?: CellValueType;
    formula?: CellFormula | null;
    elementIds: string[];
    effectiveFrom?: string | null;
    effectiveTo?: string | null;
  }) {
    return firstValueFrom(this.http.post<{ id: string }>('/v1/rating/cells', payload));
  }

  async updateCell(
    id: string,
    payload: {
      planId?: string;
      value?: number;
      valueType?: CellValueType;
      formula?: CellFormula | null;
      effectiveFrom?: string | null;
      effectiveTo?: string | null;
    }
  ) {
    return firstValueFrom(this.http.patch<void>(`/v1/rating/cells/${id}`, payload));
  }

  async setCellRecipient(id: string, recipient: ContractLedger.RecipientAssignment | null) {
    return firstValueFrom(this.http.put<{ id: string }>(`/v1/rating/cells/${id}/recipient`, { recipient }));
  }

  async setElementRecipient(id: string, recipient: ContractLedger.RecipientAssignment | null) {
    return firstValueFrom(this.http.put<{ id: string }>(`/v1/rating/elements/${id}/recipient`, { recipient }));
  }

  async listRecipientReviewCells(planId: string) {
    return firstValueFrom(
      this.http.get<RecipientReviewCell[]>('/v1/rating/cells/recipient-review', { params: { planId } })
    );
  }

  async listParties(params?: { role?: ContractLedger.PartyType; search?: string }) {
    return firstValueFrom(
      this.http.get<PartyListItem[]>('/v1/rating/parties', {
        params: { ...(params?.role && { role: params.role }), ...(params?.search && { search: params.search }) },
      })
    );
  }

  async deleteParty(id: string) {
    return firstValueFrom(this.http.delete<{ id: string }>(`/v1/rating/parties/${id}`));
  }

  async saveParty(payload: { name: string; roles: ContractLedger.PartyType[] }, id?: string) {
    return firstValueFrom(
      id
        ? this.http.patch<ContractLedger.PartyRecord>(`/v1/rating/parties/${id}`, payload)
        : this.http.post<ContractLedger.PartyRecord>('/v1/rating/parties', payload)
    );
  }

  async deleteCell(id: string) {
    return firstValueFrom(this.http.delete<void>(`/v1/rating/cells/${id}`));
  }

  async calculatePricingPreview(payload: {
    planId: string;
    planTitle: string;
    termId: string;
    termTitle: string;
    termDuration: number;
    coverageIds: string[];
    propertyValues?: Record<string, unknown>;
    pricingDimensionIds?: string[];
    dimensionValues?: Record<string, unknown>;
    asOfDate?: string;
    distributorId?: string;
  }): Promise<PricingPreview> {
    return firstValueFrom(this.http.post<PricingPreview>('/v1/rating/preview/calculate', payload));
  }
}
